import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../layouts/PageTitle";
import { Formik } from "formik";
import * as Yup from "yup";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";
import { filterActiveMasters } from "../utils/masterFilters";

const DepartmentSchema = Yup.object().shape({
  Name: Yup.string().required("Department name is required"),
});

const AddEdit_DepartmentMaster = () => {
  const { firmId: sessionFirmId, branchId: sessionBranchId } = useSession();
  const defaultFirmId = sessionFirmId ? String(sessionFirmId) : "";
  const defaultBranchId = sessionBranchId ? String(sessionBranchId) : "";

  const [state, setState] = useState({
    id: 0,
    formData: {
      Name: "",
      F_FirmMaster: defaultFirmId,
      F_BranchMaster: defaultBranchId,
      Ordering: "",
      IsSundayInclude: false,
      IsDirectWages: false,
      IsNotInUse: false,
    },
    FirmMaster: [],
    BranchMaster: [],
    isProgress: false,
  });

  const [payrollParams, setPayrollParams] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const API_URL_SAVE = "DepartmentMaster/0/token";
  const API_URL_EDIT = `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id`;

  useEffect(() => {
    Fn_FillListData(dispatch, setState, "FirmMaster", `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`);

    const Id = (location.state && location.state.Id) || 0;
    if (Id > 0) {
      setState((prev) => ({ ...prev, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    } else {
      setState((prev) => ({
        ...prev,
        formData: {
          ...prev.formData,
          F_FirmMaster: defaultFirmId,
          F_BranchMaster: defaultBranchId,
        },
      }));
    }
  }, [dispatch, location.state, defaultFirmId, defaultBranchId]);

  // Restore branches and payroll params when editing an existing record
  useEffect(() => {
    if (state.formData && state.formData.F_FirmMaster) {
      Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${state.formData.F_FirmMaster}`);
      loadPayrollParams(state.formData.F_FirmMaster);
    }
  }, [state.formData.F_FirmMaster]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPayrollParams = (firmId) => {
    if (!firmId) { setPayrollParams(null); return; }
    Fn_FillListData(dispatch, () => {}, "Data", `${API_WEB_URLS.MASTER}/0/token/PayrollParameter/TBL.F_FirmMaster/${firmId}`)
      .then(res => setPayrollParams(Array.isArray(res) && res.length > 0 ? res[0] : null))
      .catch(() => setPayrollParams(null));
  };

  const handleFirmChange = (firmId, handleChange, e) => {
    handleChange(e);
    if (firmId) {
      Fn_FillListData(dispatch, setState, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`);
      loadPayrollParams(firmId);
    } else {
      setState((prev) => ({ ...prev, BranchMaster: [] }));
      setPayrollParams(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const isSundayDeptWise = !!(payrollParams?.IsSundayDeptWise || payrollParams?.SundayIncludeDeptwise);
      const isDirectWagesDeptWise = !!(payrollParams?.IsDirectWagesDeptWise || payrollParams?.DirectWagesDepartmentwise);
      const formData = new FormData();
      formData.append("Name", values.Name);
      formData.append("F_FirmMaster", values.F_FirmMaster || defaultFirmId || 0);
      formData.append("F_BranchMaster", values.F_BranchMaster || defaultBranchId || 0);
      formData.append("Ordering", values.Ordering);
      formData.append("IsSundayInclude", isSundayDeptWise ? (values.IsSundayInclude ? true : false) : false);
      formData.append("IsDirectWages", isDirectWagesDeptWise ? (values.IsDirectWages ? true : false) : false);
      formData.append("IsNotInUse", values.IsNotInUse ? true : false);

      Fn_AddEditData(
        dispatch,
        setState,
        { arguList: { id: state.id, formData } },
        API_URL_SAVE,
        true,
        "memberid",
        navigate,
        "/DepartmentMaster"
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while submitting the form. Please try again.");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Department Master" />

      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Department Master</h4>
            </div>
            <div className="card-body">
              <div className="basic-form">
                <Formik
                  initialValues={state.formData}
                  enableReinitialize
                  validationSchema={DepartmentSchema}
                  onSubmit={(values, { setSubmitting }) => {
                    setTimeout(() => {
                      handleSubmit(values);
                      setSubmitting(false);
                    }, 400);
                  }}
                >
                  {({ values, errors, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="text-label">Department Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            name="Name"
                            value={values.Name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.Name && <div className="text-danger fs-12">{errors.Name}</div>}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">
                            Firm {defaultFirmId ? <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span> : null}
                          </label>
                          <select
                            className="form-control"
                            name="F_FirmMaster"
                            value={values.F_FirmMaster || defaultFirmId}
                            disabled={!!defaultFirmId}
                            style={defaultFirmId ? { backgroundColor: "#f8f9fa", cursor: "not-allowed" } : {}}
                            onBlur={handleBlur}
                            onChange={(e) => handleFirmChange(e.target.value, handleChange, e)}
                          >
                            <option value="">-- Select Firm --</option>
                            {state.FirmMaster
                              .filter((f) => !f.IsNotInUse || String(f.Id) === String(values.F_FirmMaster || defaultFirmId))
                              .map((f) => <option key={f.Id} value={f.Id}>{f.Name}</option>)}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Branch</label>
                          <select
                            className="form-control"
                            name="F_BranchMaster"
                            value={values.F_BranchMaster}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Branch --</option>
                            {filterActiveMasters(state.BranchMaster, values.F_BranchMaster).map((b) => <option key={b.Id} value={b.Id}>{b.Name}</option>)}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="text-label">Ordering</label>
                          <input
                            type="number"
                            className="form-control"
                            name="Ordering"
                            value={values.Ordering}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        {((payrollParams?.IsSundayDeptWise || payrollParams?.SundayIncludeDeptwise) || (payrollParams?.IsDirectWagesDeptWise || payrollParams?.DirectWagesDepartmentwise)) && (
                          <div className="col-md-6 mb-3 d-flex align-items-center mt-2">
                            {(payrollParams?.IsSundayDeptWise || payrollParams?.SundayIncludeDeptwise) && (
                              <div className="form-check me-4">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="IsSundayInclude"
                                  name="IsSundayInclude"
                                  checked={!!values.IsSundayInclude}
                                  onChange={(e) => setFieldValue("IsSundayInclude", e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="IsSundayInclude">
                                  Sunday Include
                                </label>
                              </div>
                            )}
                            {(payrollParams?.IsDirectWagesDeptWise || payrollParams?.DirectWagesDepartmentwise) && (
                              <div className="form-check me-4">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="IsDirectWages"
                                  name="IsDirectWages"
                                  checked={!!values.IsDirectWages}
                                  onChange={(e) => setFieldValue("IsDirectWages", e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="IsDirectWages">
                                  Direct Wages
                                </label>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="col-md-6 mb-3 d-flex align-items-center mt-4">
                          <div className="form-check">
                              <input
                              type="checkbox"
                              className="form-check-input"
                              id="IsNotInUse"
                              name="IsNotInUse"
                              checked={!!values.IsNotInUse}
                              onChange={(e) => setFieldValue("IsNotInUse", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="IsNotInUse">
                              Not In Use
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3">
                        <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting}>
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        {state.id > 0 && (
                          <button
                            type="button"
                            className="btn btn-danger me-2"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete department "${values.Name || state.id}"?`)) {
                                try {
                                  await Fn_DeleteData(dispatch, () => {}, Number(state.id), `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster`);
                                  toast.success("Department deleted successfully");
                                  navigate("/DepartmentMaster");
                                } catch (err) {
                                  console.error("Error deleting department:", err);
                                }
                              }
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger light"
                          onClick={() => navigate("/DepartmentMaster")}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddEdit_DepartmentMaster;
